import type { AgentView, MessageView, PersonView } from "@walkie/protocol"

/**
 * Fields the server adds when a message is withdrawn. Optional so the web app
 * works against a server that does not send them yet.
 */
export type MessageX = MessageView & {
	withdrawn?: boolean
	withdrawnAt?: string | null
	/** Handle or user id of whoever withdrew it, when the server says. */
	withdrawnBy?: string | null
}

export const isWithdrawn = (m: MessageView): boolean =>
	!!(m as MessageX).withdrawn || !!(m as MessageX).withdrawnAt

export const withdrawnAt = (m: MessageView): string | null =>
	(m as MessageX).withdrawnAt ?? null

/** Who withdrew it: a handle when known, else the author (the usual case). */
export function withdrawnBy(m: MessageView, people: PersonView[]): string {
	const by = (m as MessageX).withdrawnBy
	if (by) {
		const p = people.find(x => x.userId === by || x.handle === by)
		if (p) return p.handle
		if (!/^[0-9a-f-]{36}$/i.test(by)) return by.replace(/^@/, "")
	}
	return m.author.handle
}

export const isGithub = (m: MessageView) => m.author.type === "github"

/** First line of a message, for one-line references to it. */
export const oneLine = (m: MessageView, max = 120) =>
	(m.title ?? m.body.split("\n")[0] ?? "").slice(0, max)

/** An agent stops counting as present once ended, or offline for this long. */
export const GONE_AFTER_MS = 30 * 60_000

export function agentGone(a: AgentView | undefined, now: number): boolean {
	if (!a) return false
	if (a.state === "ended") return true
	if (a.state !== "offline") return false
	const seen = a.lastSeenAt ? Date.parse(a.lastSeenAt) : 0
	return now - seen > GONE_AFTER_MS
}

/**
 * The agent responsible for an open request: the claimer while claimed,
 * otherwise the owner. Returns it when that agent is gone (orphaned request).
 */
export function orphanedBy(
	m: MessageView,
	agents: Record<string, AgentView>,
	now: number,
): AgentView | null {
	const r = m.request
	if (!r || isWithdrawn(m)) return null
	const holder =
		r.status === "claimed"
			? (r.claimedByAgentId ?? null)
			: r.status === "open"
				? r.ownerAgentId
				: null
	if (!holder) return null
	const a = agents[holder]
	return a && agentGone(a, now) ? a : null
}
